import { Download } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import CustomButton from 'lib/designSystem/Button';
import React, { useEffect, useState } from 'react';

import HeaderWithActions from '../../../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../../../lib/designSystem/Table';
import { formatDate } from '../../../../../../../../lib/functions/utils';

interface Document {
  id: string;
  businessIdentifier: string;
  businessName: string;
  operationDate: string;
  documentNumber: string;
  leftAmountNet: number;
  leftAmount: number;
  amount: number;
  dueDate: string;
  lastConciliationDate: string;
  newPaymentDate: string;
  arrearRate: string;
  arrearDays: number;
  arrear: number;
}

interface DebtProjectorProps {
  open: boolean;
  documents: Document[];
  setOpen: (open: boolean) => void;
}

const excelHeaders = [
  'Rut cliente',
  'Nombre cliente',
  'Fecha operación',
  'Número documento',
  'Saldo documento',
  'Saldo documento neto',
  'Fecha vencimiento',
  'Fecha último abono',
  'Fecha proyectada de pago',
  'Tasa mora (%)',
  'Días mora',
  'Mora proyectada',
];

const DebtProjector: React.FC<DebtProjectorProps> = (props) => {
  const { setOpen } = props;
  const [documents, setDocuments] = useState<Document[]>(props.documents);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentDateType, setPaymentDateType] = useState(
    'lastConciliationDate',
  );
  const [amountType, setAmountType] = useState('leftAmount');
  const getAmount = (item: Document) => {
    console.log(item);
    if (amountType === 'leftAmount') {
      return item.leftAmount;
    } else if (amountType === 'leftAmountNet') {
      return item.leftAmountNet;
    } else if (amountType === 'documentAmount') {
      return item.amount;
    } else {
      return 0;
    }
  };
  const exportToExcel = async () => {
    const plainData = documents.map((item: Document) => {
      return [
        item.businessIdentifier,
        item.businessName,
        formatDate(new Date(item.operationDate)),
        item.documentNumber,
        item.leftAmount,
        item.leftAmountNet,
        formatDate(new Date(item.dueDate)),
        formatDate(new Date(item.lastConciliationDate)),
        formatDate(new Date(item.newPaymentDate)),
        item.arrearRate,
        item.arrearDays,
        item.arrear,
      ];
    });
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Add data to worksheet
    [excelHeaders, ...plainData].forEach((rowData, rowIndex) => {
      const row = worksheet.addRow(rowData);
      if (rowIndex === 0) {
        // Make the first row bold, visible, and add grey background color
        row.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          worksheet.getColumn(cell.col).width = 20;
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDDDDDD' }, // Grey background color
          };
        });
      }
    });
    // Save workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `Proyección_Moras_${formatDate(paymentDate.toDate())}.xlsx`;
    a.click();

    URL.revokeObjectURL(url);
  };

  const calculateArrear = () => {
    const newDocuments = [];
    let documentsToReview;
    if (documents.length === 0) {
      documentsToReview = props.documents;
    } else {
      documentsToReview = documents;
    }
    for (const document of documentsToReview) {
      const days = dayjs(paymentDate).diff(
        dayjs(
          paymentDateType === 'lastConciliationDate'
            ? document.lastConciliationDate
            : document.dueDate,
        ),
        'day',
      );
      const daysReal = days > 0 ? days : 0;
      const amount = getAmount(document);
      const arrear =
        ((amount * Number(document.arrearRate)) / 100 / 30) * daysReal;
      const newDocument = {
        ...document,
        arrearDays: daysReal,
        arrear: Math.round(arrear),
        newPaymentDate: formatDate(paymentDate.toDate()),
      };
      newDocuments.push(newDocument);
    }
    setDocuments(newDocuments);
  };

  const handleEditArrearRate = (rowId: string, value: string) => {
    // Control value
    if (isNaN(Number(value))) {
      return;
    }
    if (value === '') {
      value = '0';
    }
    if (Number(value) > 100 || Number(value) < 0) {
      return;
    }

    const updatedDocuments = [];
    const documentsToReview = documents;
    for (const document of documentsToReview) {
      let arrearRate = document.arrearRate;
      if (document.id === rowId) {
        arrearRate = value;
      }
      const days = dayjs(paymentDate).diff(
        dayjs(
          paymentDateType === 'lastConciliationDate'
            ? document.lastConciliationDate
            : document.dueDate,
        ),
        'day',
      );
      const daysReal = days > 0 ? days : 0;
      const amount = getAmount(document);
      const arrear = ((amount * Number(arrearRate)) / 100 / 30) * daysReal;
      const newDocument = {
        ...document,
        arrearRate,
        arrearDays: daysReal,
        arrear: Math.round(arrear),
        newPaymentDate: formatDate(paymentDate.toDate()),
      };
      updatedDocuments.push(newDocument);
    }
    setDocuments(updatedDocuments);
  };

  const getAmountHeaderObject = () => {
    if (amountType === 'leftAmountNet') {
      return {
        key: 'leftAmountNet',
        label: 'Monto a proyectar',
        type: 'money',
      };
    } else if (amountType === 'leftAmount') {
      return {
        key: 'leftAmount',
        label: 'Monto a proyectar',
        type: 'money',
      };
    } else {
      return {
        key: 'amount',
        label: 'Monto a proyectar',
        type: 'money',
      };
    }
  };

  const headers = [
    {
      key: 'businessIdentifier',
      label: 'Rut cliente',
      type: 'report',
    },
    { key: 'businessName', label: 'Nombre cliente' },
    { key: 'operationDate', label: 'Fecha operación', type: 'date' },
    { key: 'documentNumber', label: 'Número documento' },
    { key: 'amount', label: 'Monto Doc.', type: 'money' },
    { key: 'leftAmount', label: 'Saldo doc.', type: 'money' },
    { key: 'leftAmountNet', label: 'Saldo doc. neto', type: 'money' },
    { key: 'dueDate', label: 'Vencimiento', type: 'date' },
    { key: 'lastConciliationDate', label: 'Último abono', type: 'date' },
    { key: 'newPaymentDate', label: 'Fecha proyectada de pago' },
    {
      key: 'arrearRate',
      label: 'Tasa mora (%)',
      type: 'editable-arrear-rate',
      customAction: handleEditArrearRate,
    },
    { key: 'arrearDays', label: 'Días mora' },
    ...[getAmountHeaderObject()],
    { key: 'arrear', label: 'Mora proyectada', type: 'money' },
  ];

  useEffect(() => {
    calculateArrear();
  }, [paymentDate, props.documents, paymentDateType, amountType]);

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={() => props.setOpen(false)}
        fullWidth={true}
        maxWidth="xl"
      >
        <DialogContent>
          <div>
            <Typography variant="h4">Proyectar mora</Typography>
            <HeaderWithActions
              buttons={[
                {
                  action: () => void exportToExcel(),
                  name: 'Descargar XLSX',
                  color: 'primary',
                  startIcon: <Download />,
                },
              ]}
              customTitle={
                <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                  <FormControl>
                    <InputLabel>Fecha inicio</InputLabel>
                    <Select
                      fullWidth
                      label="Fecha inicio"
                      value={paymentDateType}
                      onChange={(e) => setPaymentDateType(e.target.value)}
                    >
                      <MenuItem value="lastConciliationDate">
                        Último abono
                      </MenuItem>
                      <MenuItem value="dueDate">Vencimiento</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <InputLabel>Fecha inicio</InputLabel>
                    <Select
                      fullWidth
                      label="Fecha inicio"
                      value={amountType}
                      onChange={(e) => setAmountType(e.target.value)}
                    >
                      <MenuItem value="leftAmountNet">
                        Saldo anticipado
                      </MenuItem>
                      <MenuItem value="leftAmount">Saldo documento</MenuItem>
                      <MenuItem value="documentAmount">
                        Monto documento
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es-mx"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      label="Nueva fecha"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(dayjs(e))}
                    />
                  </LocalizationProvider>
                </div>
              }
            />

            <EnhancedTable dataHeaders={headers} data={documents} />
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton color="primary" onClick={() => setOpen(false)}>
            Salir
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DebtProjector;
